import { getExtension } from '@medplum/core';
import { Attachment, Communication, Practitioner, Reference } from '@medplum/fhirtypes';
import { isProviderRoutingHistoryEntry } from './call-routing-history';
import { getCommunicationDirection, isForwardedCommunication } from './communication';
import {
  CommunicationDirection,
  TWILIO_DIAL_CALL_STATUS_MESSAGE,
  TWILIO_SYSTEM,
  VOICE_RECORDING_PAYLOAD_ID,
} from './constants';
import {
  INCOMING_CALL_ROUTING_HISTORY_EXTENSION,
  TWILIO_AMD_RESULT_EXTENSION,
  TWILIO_CALL_HISTORY_EXTENSION,
  TWILIO_CONFERENCE_SESSION_EXTENSION,
} from './extensions';
import type { TwilioAnsweredBy } from './twilio-voice';

/** Outbound calls shorter than this are treated as failed connections (airplane mode, dead air). */
const MEANINGFUL_OUTBOUND_RECORDING_SECONDS = 8;

export function isVoiceRecordingPayloadId(payloadId: string | undefined): boolean {
  if (!payloadId) {
    return false;
  }
  return payloadId === VOICE_RECORDING_PAYLOAD_ID || payloadId.startsWith(`${VOICE_RECORDING_PAYLOAD_ID}-`);
}

export type TwilioCallErrorPayload = {
  errorCode?: string;
  errorMessage?: string;
  timestamp?: string;
  recordingStatus?: string;
};

export function isMeaningfulTwilioCallError(error: TwilioCallErrorPayload): boolean {
  if (error.errorMessage) {
    return true;
  }
  const code = String(error.errorCode ?? '');
  return code !== '' && code !== '0';
}

export const VoiceCallOutcome = {
  ANSWERED: 'answered',
  INCOMING_MISSED: 'incoming-missed',
  INCOMING_CALLER_HUNG_UP: 'incoming-caller-hung-up',
  /**
   * Nobody here answered, and the caller was dialled through to the answering
   * service (or the sales line) instead.
   *
   * The nearest thing this system has to a voicemail: there is no mailbox
   * anywhere in the IVR, so an unanswered call ends up with a human at
   * AnswerConnect, whose message comes back later as a `virtual-receptionist`
   * payload. Kept distinct from `INCOMING_MISSED` because it is a callback owed
   * rather than a lost contact.
   */
  INCOMING_FORWARDED: 'incoming-forwarded',
  OUTGOING_PATIENT_NO_ANSWER: 'outgoing-patient-no-answer',
  OUTGOING_CANCELED: 'outgoing-canceled',
  OUTGOING_BUSY: 'outgoing-busy',
  OUTGOING_FAILED: 'outgoing-failed',
  UNKNOWN_UNANSWERED: 'unknown-unanswered',
} as const;

export type VoiceCallOutcome = (typeof VoiceCallOutcome)[keyof typeof VoiceCallOutcome];

export type UnansweredVoiceCallOutcome = Exclude<VoiceCallOutcome, typeof VoiceCallOutcome.ANSWERED>;

export type TwilioVoiceStatusPayload = {
  CallStatus?: string;
  DialCallStatus?: string;
  Duration?: string;
  CallDuration?: string;
  dialCallStatusMessage?: string;
};

export type TwilioRecordingCallbackPayload = {
  RecordingStatus?: string;
  RecordingUrl?: string;
  RecordingDuration?: string;
};

function decodeBase64Json<T>(data: string): T | undefined {
  try {
    const json = typeof Buffer !== 'undefined' ? Buffer.from(data, 'base64').toString('utf-8') : atob(data);
    return JSON.parse(json) as T;
  } catch {
    return undefined;
  }
}

export const VOICE_CALL_OUTCOME_RECORDING_MESSAGE: Record<UnansweredVoiceCallOutcome, string> = {
  [VoiceCallOutcome.INCOMING_MISSED]: 'No one was available to answer this call. No recording is available.',
  [VoiceCallOutcome.INCOMING_CALLER_HUNG_UP]: 'The caller hung up before anyone answered. No recording is available.',
  [VoiceCallOutcome.INCOMING_FORWARDED]: 'No one here answered; the call was forwarded on. No recording is available.',
  [VoiceCallOutcome.OUTGOING_PATIENT_NO_ANSWER]: `${TWILIO_DIAL_CALL_STATUS_MESSAGE['no-answer']} No recording is available.`,
  [VoiceCallOutcome.OUTGOING_CANCELED]: 'The call was ended before the patient answered. No recording is available.',
  [VoiceCallOutcome.OUTGOING_BUSY]: `${TWILIO_DIAL_CALL_STATUS_MESSAGE.busy} No recording is available.`,
  [VoiceCallOutcome.OUTGOING_FAILED]: `${TWILIO_DIAL_CALL_STATUS_MESSAGE.failed} No recording is available.`,
  [VoiceCallOutcome.UNKNOWN_UNANSWERED]: 'This call was not recorded.',
};

/** @deprecated Use VOICE_CALL_DISPLAY_BADGE via getVoiceCallDisplayStatus for timeline badges. */
export const VOICE_CALL_OUTCOME_BADGE: Record<VoiceCallOutcome, { label: string; color: string }> = {
  [VoiceCallOutcome.ANSWERED]: { label: 'completed', color: 'green' },
  [VoiceCallOutcome.INCOMING_MISSED]: { label: 'missed call', color: 'red' },
  [VoiceCallOutcome.INCOMING_CALLER_HUNG_UP]: { label: 'caller hung up', color: 'var(--mantine-color-orange-7)' },
  [VoiceCallOutcome.INCOMING_FORWARDED]: { label: 'forwarded', color: 'var(--mantine-color-orange-7)' },
  [VoiceCallOutcome.OUTGOING_PATIENT_NO_ANSWER]: { label: 'no answer', color: 'var(--mantine-color-orange-7)' },
  [VoiceCallOutcome.OUTGOING_CANCELED]: { label: 'canceled', color: 'gray' },
  [VoiceCallOutcome.OUTGOING_BUSY]: { label: 'busy', color: 'red' },
  [VoiceCallOutcome.OUTGOING_FAILED]: { label: 'failed', color: 'red' },
  [VoiceCallOutcome.UNKNOWN_UNANSWERED]: { label: 'unanswered', color: 'gray' },
};

export type VoiceCallDisplayStatus =
  | 'preparing'
  | 'in-progress'
  | 'completed'
  | 'missed-call'
  | 'forwarded'
  | 'no-answer'
  | 'failed';

export const VOICE_CALL_DISPLAY_BADGE: Record<VoiceCallDisplayStatus, { label: string; color: string }> = {
  preparing: { label: 'preparing', color: 'blue' },
  'in-progress': { label: 'in progress', color: 'blue' },
  completed: { label: 'completed', color: 'green' },
  'missed-call': { label: 'missed call', color: 'red' },
  // Its own badge rather than a shade of "missed": the caller did reach a person,
  // and someone here owes them a call back. Labelled for the hand-off rather than
  // its destination, because the same outcome covers the answering service and
  // the sales line.
  forwarded: { label: 'forwarded', color: 'var(--mantine-color-orange-7)' },
  'no-answer': { label: 'no answer', color: 'var(--mantine-color-orange-7)' },
  failed: { label: 'failed', color: 'var(--mantine-color-orange-7)' },
};

function mapOutcomeToDisplayStatus(outcome: VoiceCallOutcome): VoiceCallDisplayStatus {
  switch (outcome) {
    case VoiceCallOutcome.ANSWERED:
      return 'completed';
    case VoiceCallOutcome.INCOMING_MISSED:
    case VoiceCallOutcome.INCOMING_CALLER_HUNG_UP:
      return 'missed-call';
    case VoiceCallOutcome.INCOMING_FORWARDED:
      return 'forwarded';
    case VoiceCallOutcome.OUTGOING_PATIENT_NO_ANSWER:
    case VoiceCallOutcome.OUTGOING_CANCELED:
    case VoiceCallOutcome.OUTGOING_BUSY:
      return 'no-answer';
    case VoiceCallOutcome.OUTGOING_FAILED:
    case VoiceCallOutcome.UNKNOWN_UNANSWERED:
      return 'failed';
  }
}

function unresolvedIncomingDisplayStatus(direction: CommunicationDirection | undefined): VoiceCallDisplayStatus {
  return direction === CommunicationDirection.INCOMING ? 'missed-call' : 'failed';
}

export function getVoiceCallDisplayStatus(params: {
  callOutcome?: VoiceCallOutcome;
  direction?: CommunicationDirection;
  fhirStatus?: Communication['status'];
}): VoiceCallDisplayStatus | undefined {
  const { callOutcome, direction, fhirStatus } = params;

  if (callOutcome) {
    return mapOutcomeToDisplayStatus(callOutcome);
  }

  switch (fhirStatus) {
    case 'preparation':
      return 'preparing';
    case 'in-progress':
      return 'in-progress';
    case 'completed':
      return 'completed';
    case 'stopped':
    case 'entered-in-error':
      return unresolvedIncomingDisplayStatus(direction);
    case 'not-done':
      return 'failed';
    default:
      return undefined;
  }
}

/**
 * The display status of a call, derived from what actually happened on it.
 *
 * `Communication.status` describes the *record*, not the call: a missed inbound
 * call completes normally, so anything reading `status` alone reports it as an
 * ordinary completed call. Callers holding the whole Communication should use
 * this rather than passing `fhirStatus` to `getVoiceCallDisplayStatus` on its
 * own.
 */
export function deriveVoiceCallDisplayStatus(communication: Communication): VoiceCallDisplayStatus | undefined {
  return getVoiceCallDisplayStatus({
    callOutcome: deriveVoiceCallOutcome(communication),
    direction: getCommunicationDirection(communication),
    fhirStatus: communication.status,
  });
}

const UNANSWERED_VOICE_CALL_DISPLAY_STATUSES: VoiceCallDisplayStatus[] = [
  'missed-call',
  'forwarded',
  'no-answer',
  'failed',
];

/** Nobody here was on the line: the call is owed a callback rather than logged as a conversation. */
export function isUnansweredVoiceCallDisplayStatus(status: VoiceCallDisplayStatus | undefined): boolean {
  return status !== undefined && UNANSWERED_VOICE_CALL_DISPLAY_STATUSES.includes(status);
}

function decodeBase64JsonVoiceStatus(data: string): TwilioVoiceStatusPayload | undefined {
  return decodeBase64Json<TwilioVoiceStatusPayload>(data);
}

export function parseTwilioVoiceStatusPayload(data: string | undefined): TwilioVoiceStatusPayload | undefined {
  if (!data) {
    return undefined;
  }
  return decodeBase64JsonVoiceStatus(data);
}

export function getTwilioCallHistoryEntries(communication: Communication) {
  return getExtension(communication, TWILIO_CALL_HISTORY_EXTENSION)?.extension ?? [];
}

/** Recording status callbacks stored on the register, oldest first. */
export function getTwilioRecordingCallbackHistory(communication: Communication): TwilioRecordingCallbackPayload[] {
  return getTwilioCallHistoryEntries(communication)
    .filter((entry) => entry.url === 'recording-callback')
    .map((entry) => decodeBase64Json<TwilioRecordingCallbackPayload>(entry.valueAttachment?.data ?? ''))
    .filter((payload): payload is TwilioRecordingCallbackPayload => payload !== undefined);
}

/**
 * `twilio-call-history` entry url the routing callback writes the inbound leg's
 * dial outcome under. Shared so the bot that writes it and the derivation that
 * reads it cannot drift apart.
 */
export const INBOUND_DIAL_STATUS_HISTORY_URL = 'routing-dial-status';

/** Dial outcomes recorded for the inbound leg, oldest first. */
export function getInboundDialCallStatuses(communication: Communication): string[] {
  return getTwilioCallHistoryEntries(communication)
    .filter((entry) => entry.url === INBOUND_DIAL_STATUS_HISTORY_URL)
    .map((entry) => decodeBase64Json<{ DialCallStatus?: string }>(entry.valueAttachment?.data ?? '')?.DialCallStatus)
    .filter((status): status is string => Boolean(status));
}

/** One press of the IVR menu, as `annotateCallsWithIvrInteraction` records it. */
export interface IvrSelection {
  templateId?: string;
  digit?: number;
}

/**
 * Menu selections recorded for this call, oldest first.
 *
 * Lives beside the other Twilio call-history readers because it shares their
 * plumbing: the same extension, the same base64 payloads.
 */
export function getIvrSelections(communication: Communication): IvrSelection[] {
  return getTwilioCallHistoryEntries(communication)
    .filter((entry) => entry.url === 'ivr-interaction')
    .map((entry) => decodeBase64Json<IvrSelection>(entry.valueAttachment?.data ?? ''))
    .filter((selection): selection is IvrSelection => selection !== undefined);
}

/**
 * The press that decided where the call went — the last one.
 *
 * Absent when the caller heard the menu and chose nothing, which in production
 * is most of them.
 */
export function getLastIvrSelection(communication: Communication): IvrSelection | undefined {
  const selections = getIvrSelections(communication);
  return selections[selections.length - 1];
}

/**
 * Twilio call SIDs recorded on the Communication.
 *
 * The inbound leg's SID is written when the call is first registered, and
 * `twilio-voice-routing-status-callback` merges a second one the moment the
 * agent's client leg goes in-progress. More than one SID therefore means a
 * provider actually picked the call up.
 */
function getTwilioCallLegCount(communication: Communication): number {
  return (communication.identifier ?? []).filter((identifier) => identifier.system === TWILIO_SYSTEM).length;
}

/**
 * Did somebody here actually pick this inbound call up?
 *
 * Deliberately **not** "is there a recording". An inbound call starts a
 * `<Start><Recording>` session the moment the IVR answers, before any routing at
 * all, so a recording exists on calls nobody ever took — including the ones
 * dialled straight through to the answering service. Reading a recording as an
 * answer reported almost every inbound call as picked up (BAR-2519).
 *
 * The signals that do mean an answer, in order of reliability:
 *
 * 1. a `voice-status` webhook that saw the call in progress;
 * 2. a conference session, which only exists once legs are bridged;
 * 3. a second Twilio call SID, merged on when the agent's leg connects;
 * 4. a recorded dial outcome of `completed`/`answered`, which is the only signal
 *    available when a provider answers on a plain phone rather than in the app.
 *
 * (4) is only present on calls that came in after the routing callback started
 * recording it, so older calls answered on a cell phone read as unanswered.
 */
function wasIncomingCallAnswered(communication: Communication, history: TwilioVoiceStatusPayload[]): boolean {
  if (history.some((entry) => entry.CallStatus === 'in-progress')) {
    return true;
  }

  if (getExtension(communication, TWILIO_CONFERENCE_SESSION_EXTENSION)?.valueString) {
    return true;
  }

  if (getTwilioCallLegCount(communication) > 1) {
    return true;
  }

  return getInboundDialCallStatuses(communication).some((status) => status === 'completed' || status === 'answered');
}

/**
 * Whoever picked this inbound call up, if anybody did.
 *
 * `recipient` names the provider the call is currently offered to, the routing
 * bots collapse it to the answerer once somebody takes it, and forwarding
 * clears it. So it identifies an answerer only on a call that reads as
 * answered — the same rule the PAC daily report counts pickups by. Anywhere
 * else it names someone who let the phone ring.
 */
export function getIncomingCallAnswerer(communication: Communication): Reference<Practitioner> | undefined {
  if (getCommunicationDirection(communication) !== CommunicationDirection.INCOMING) {
    return undefined;
  }

  if (!wasIncomingCallAnswered(communication, getTwilioVoiceStatusHistory(communication))) {
    return undefined;
  }

  return communication.recipient?.[0] as Reference<Practitioner> | undefined;
}

/**
 * Longest call duration Twilio reported, in seconds. Zero when it has reported none —
 * which is what a call still in progress looks like, so render that as live rather than
 * as a call that lasted no time at all.
 *
 * A call accumulates one `voice-status` entry per webhook and several carry a
 * `CallDuration`. The last one is the whole call, but taking the max rather than the last
 * avoids depending on webhook arrival order.
 */
export function getCallDurationSeconds(communication: Communication): number {
  const durations = getTwilioVoiceStatusHistory(communication)
    .map((entry) => parsePositiveDuration(entry.CallDuration))
    .filter((duration) => duration > 0);

  return durations.length > 0 ? Math.max(...durations) : 0;
}

export function getTwilioVoiceStatusHistory(communication: Communication): TwilioVoiceStatusPayload[] {
  return getTwilioCallHistoryEntries(communication)
    .filter((entry) => entry.url === 'voice-status')
    .map((entry) => parseTwilioVoiceStatusPayload(entry.valueAttachment?.data))
    .filter((payload): payload is TwilioVoiceStatusPayload => payload !== undefined);
}

export function getVoiceRecordingPayloadAttachments(communication: Communication): Attachment[] {
  return (communication.payload ?? [])
    .filter((payload) => isVoiceRecordingPayloadId(payload.id) && payload.contentAttachment?.url)
    .map((payload) => payload.contentAttachment as Attachment);
}

export function hasVoiceRecording(communication: Communication): boolean {
  if (getVoiceRecordingPayloadAttachments(communication).length > 0) {
    return true;
  }

  return getTwilioRecordingCallbackHistory(communication).some(
    (entry) => entry.RecordingStatus === 'completed' && !!entry.RecordingUrl
  );
}

function parsePositiveDuration(value: string | undefined): number {
  if (!value) {
    return 0;
  }
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

function getTwilioAmdResult(communication: Communication): TwilioAnsweredBy | undefined {
  return getExtension(communication, TWILIO_AMD_RESULT_EXTENSION)?.valueString as TwilioAnsweredBy | undefined;
}

function isMachineOrFaxAmd(answeredBy: TwilioAnsweredBy): boolean {
  return answeredBy === 'fax' || answeredBy.includes('machine');
}

function getMaxRecordingDurationSeconds(communication: Communication): number {
  const durations = getTwilioRecordingCallbackHistory(communication)
    .map((entry) => parsePositiveDuration(entry.RecordingDuration))
    .filter((duration) => duration > 0);

  return durations.length > 0 ? Math.max(...durations) : 0;
}

function wasOutgoingProviderCanceled(history: TwilioVoiceStatusPayload[]): boolean {
  return history.some((entry) => !entry.DialCallStatus && entry.CallStatus === 'canceled');
}

function wasOutgoingConnectionFailed(history: TwilioVoiceStatusPayload[], hasRecording: boolean): boolean {
  if (hasRecording) {
    return false;
  }

  if (history.some((entry) => entry.CallStatus === 'in-progress')) {
    return false;
  }

  return history.some(
    (entry) =>
      !entry.DialCallStatus &&
      entry.CallStatus === 'no-answer' &&
      parsePositiveDuration(entry.Duration) === 0 &&
      parsePositiveDuration(entry.CallDuration) === 0
  );
}

function wasCallConnected(
  history: TwilioVoiceStatusPayload[],
  communication: Communication,
  direction: CommunicationDirection | undefined
): boolean {
  if (history.some((entry) => entry.CallStatus === 'in-progress')) {
    return true;
  }

  if (direction === CommunicationDirection.OUTGOING) {
    if (
      history.some(
        (entry) =>
          entry.CallStatus === 'completed' &&
          (parsePositiveDuration(entry.CallDuration) > 0 || parsePositiveDuration(entry.Duration) > 0)
      )
    ) {
      return true;
    }

    return !!getExtension(communication, TWILIO_CONFERENCE_SESSION_EXTENSION)?.valueString;
  }

  return false;
}

function isOutgoingMeaningfullyAnswered(
  communication: Communication,
  history: TwilioVoiceStatusPayload[],
  hasRecording: boolean
): boolean {
  const amd = getTwilioAmdResult(communication);
  if (amd && isMachineOrFaxAmd(amd)) {
    return false;
  }

  if (getExtension(communication, TWILIO_CONFERENCE_SESSION_EXTENSION)?.valueString) {
    return true;
  }

  const hadInProgress = history.some((entry) => entry.CallStatus === 'in-progress');
  const recordingSeconds = getMaxRecordingDurationSeconds(communication);
  const dialOutcome = findLatestDialOutcome(history);
  const dialAnswered = dialOutcome?.DialCallStatus === 'completed' || dialOutcome?.DialCallStatus === 'answered';

  if (amd === 'human') {
    return hadInProgress || dialAnswered || hasRecording;
  }

  if (hadInProgress || hasRecording || dialAnswered) {
    return recordingSeconds >= MEANINGFUL_OUTBOUND_RECORDING_SECONDS;
  }

  return wasCallConnected(history, communication, CommunicationDirection.OUTGOING);
}

function findLatestDialOutcome(history: TwilioVoiceStatusPayload[]): TwilioVoiceStatusPayload | undefined {
  for (let index = history.length - 1; index >= 0; index -= 1) {
    if (history[index]?.DialCallStatus) {
      return history[index];
    }
  }
  return undefined;
}

export function deriveVoiceCallOutcome(communication: Communication): VoiceCallOutcome | undefined {
  const { status } = communication;

  if (status === 'preparation' || status === 'in-progress') {
    return undefined;
  }

  const history = getTwilioVoiceStatusHistory(communication);
  const hasRecording = hasVoiceRecording(communication);
  const direction = getCommunicationDirection(communication);
  const isIncoming = direction === CommunicationDirection.INCOMING;

  if (!isIncoming) {
    if (wasOutgoingProviderCanceled(history)) {
      return VoiceCallOutcome.OUTGOING_CANCELED;
    }

    const dialOutcome = findLatestDialOutcome(history);
    if (dialOutcome?.DialCallStatus === 'no-answer') {
      return VoiceCallOutcome.OUTGOING_PATIENT_NO_ANSWER;
    }
    if (dialOutcome?.DialCallStatus === 'busy') {
      return VoiceCallOutcome.OUTGOING_BUSY;
    }
    if (dialOutcome?.DialCallStatus === 'failed') {
      return VoiceCallOutcome.OUTGOING_FAILED;
    }

    const amd = getTwilioAmdResult(communication);
    if (amd && isMachineOrFaxAmd(amd)) {
      return VoiceCallOutcome.OUTGOING_PATIENT_NO_ANSWER;
    }

    if (isOutgoingMeaningfullyAnswered(communication, history, hasRecording)) {
      return VoiceCallOutcome.ANSWERED;
    }

    const hadConnectionAttempt = history.some((entry) => entry.CallStatus === 'in-progress') || hasRecording;
    if (hadConnectionAttempt) {
      return VoiceCallOutcome.OUTGOING_FAILED;
    }

    if (wasOutgoingConnectionFailed(history, hasRecording)) {
      return VoiceCallOutcome.OUTGOING_FAILED;
    }

    if (status === 'completed' || status === 'stopped') {
      return VoiceCallOutcome.UNKNOWN_UNANSWERED;
    }
    return undefined;
  }

  if (wasIncomingCallAnswered(communication, history)) {
    return VoiceCallOutcome.ANSWERED;
  }

  if (status !== 'completed' && status !== 'stopped') {
    return undefined;
  }

  // Forwarding beats routing history: a call offered to a coordinator and then
  // handed on still left the building — to the answering service for a known
  // patient, to the sales line for an unknown caller — and that hand-off is what
  // the caller experienced and what someone has to follow up.
  if (isForwardedCommunication(communication)) {
    return VoiceCallOutcome.INCOMING_FORWARDED;
  }

  // Only the provider entries count. A chain whose every rung was skipped writes skip
  // entries into the same container without ever ringing anyone (BAR-2621), and a call
  // nobody's phone ever lit up for is not a missed call.
  const routingHistory = getExtension(communication, INCOMING_CALL_ROUTING_HISTORY_EXTENSION)?.extension;
  if (routingHistory?.some(isProviderRoutingHistoryEntry)) {
    return VoiceCallOutcome.INCOMING_MISSED;
  }

  return VoiceCallOutcome.INCOMING_CALLER_HUNG_UP;
}

export function getVoiceCallRecordingUnavailableMessage(
  outcome: VoiceCallOutcome | undefined,
  communication?: Communication
): string | undefined {
  if (!outcome || outcome === VoiceCallOutcome.ANSWERED) {
    return undefined;
  }

  if (outcome === VoiceCallOutcome.OUTGOING_PATIENT_NO_ANSWER && communication) {
    const dialOutcome = findLatestDialOutcome(getTwilioVoiceStatusHistory(communication));
    if (dialOutcome?.dialCallStatusMessage) {
      return `${dialOutcome.dialCallStatusMessage} No recording is available.`;
    }
  }

  return VOICE_CALL_OUTCOME_RECORDING_MESSAGE[outcome];
}
